import { lazy } from 'react';

const RELOAD_FLAG = 'lazy-chunk-reloaded';

// A reload is allowed again once this long has passed since the last one, so a chunk that
// fails twice in a row (e.g. the server was mid-restart) still recovers, but we can't loop.
const RELOAD_COOLDOWN_MS = 15000;

const isChunkLoadError = error => {
  const message = String(error?.message || error || '');
  return (
    error?.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /Loading (CSS )?chunk .* failed/i.test(message)
  );
};

const recentlyReloaded = () => {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_FLAG));
    return Number.isFinite(at) && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return true; // storage blocked: never reload, so we can't loop
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // ignore
  }
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * React.lazy for a page chunk. A chunk can fail to load after a deploy (the open tab asks
 * for a hash that no longer exists) or on a network blip. Retry once after a short pause,
 * then reload the page to pick up the new build (at most once per cooldown, so it can't loop).
 *
 * pick: for a named export, e.g. lazyWithReload(() => import('./X'), m => m.X).
 */
const lazyWithReload = (factory, pick) =>
  lazy(async () => {
    let module;
    try {
      module = await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      try {
        await wait(800);
        module = await factory();
      } catch {
        module = null;
      }
      if (!module) {
        if (recentlyReloaded()) throw error;
        markReloaded();
        window.location.reload();
        // Keep suspending until the reload happens.
        return new Promise(() => {});
      }
    }
    return pick ? { default: pick(module) } : module;
  });

export { isChunkLoadError };
export default lazyWithReload;
